// design §5.1: "Every card carries a small five-segment glyph marking which
// band this is." Five segments — one per similarity band on the distance
// ladder — filled cumulatively by closeness so the card's ladder position
// reads at a glance (signal-strength style): Very close lights all five,
// Far afield lights one. It carries no information the card doesn't already
// state in words and numbers (the band name + the 0-100 anchor); its only
// job is faster visual scanning and comparison across a grid of cards.
//
// Band membership is computed exactly as everywhere else — BAND_LADDER is the
// single source of the five bands and their closest-first order — so this
// glyph can never disagree with the band NAME printed beside it.
import { BAND_LADDER, bandLadderIndex } from '../../lib/whitespace'

interface BandGlyphProps {
  /** The region's band key (regionBandKey(region.band)), e.g. 'moderately_close'. */
  bandKey: string | undefined
  /** Optional class overrides for the outer wrapper. */
  className?: string
}

/**
 * A five-segment closeness meter. `bandLadderIndex` returns 0 for the closest
 * band (Very close) and 4 for the farthest (Far afield); we light
 * `5 - index` segments so closer bands read as a stronger signal. An
 * unrecognised key (index -1) lights nothing rather than guessing.
 */
export function BandGlyph({ bandKey, className = '' }: BandGlyphProps) {
  const index = bandLadderIndex(bandKey)
  const lit = index >= 0 ? BAND_LADDER.length - index : 0
  const bandName = index >= 0 ? BAND_LADDER[index].name : 'Unknown band'
  return (
    <span
      className={`inline-flex items-end gap-[2px] ${className}`}
      role="img"
      aria-label={`Band: ${bandName}`}
      data-testid="band-glyph"
      data-band-key={bandKey ?? ''}
      data-lit={lit}
      title={bandName}
    >
      {BAND_LADDER.map((_, i) => (
        <span
          key={i}
          aria-hidden="true"
          className={`w-[3px] rounded-sm ${
            i < lit ? 'bg-iflow-500' : 'bg-sky-500/20'
          }`}
          // Taller segments toward the "closer" (left) end give the meter a
          // ladder shape; heights are a fixed 6-10px ramp.
          style={{ height: `${6 + (BAND_LADDER.length - 1 - i) * 1}px` }}
        />
      ))}
    </span>
  )
}
